import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Action from './components/Action';
import { postData } from '../../../../../../api/componentAPIs';
import { constants } from './Constants';
import { ALPHA_NUMERIC_REGEX_WITH_SPACING } from '../../../../Dispatch/RouteExecution/constRegex';
import { TextFieldWithLabel } from '../../../../Dispatch/RouteExecution/Pickups/PickupManagement/components/TextField';
import { CustomDateRangePickerWithLabel } from './CustomDateRangePickerWithLabel';
import format from 'date-fns/format';
import appConstants from '../../../../../../appConstants';
import { getCustomerSpecificHolidayValidations } from './utils';
import { sanitizeInput } from '../../../../../../utilities/dataUtils';

const styles = (theme) => ({
  container: {
    display: 'flex',
    padding: '10 14 12 14',
    minHeight: '20%',
    justifyContent: 'flex-end',
    border: `1px solid ${theme.AppBGColor}`,
    borderRadius: '4px',
    flexDirection: 'column',
    maxWidth: 340
  },
  holidayTitle: {
    fontWeight: 500,
    fontSize: theme.headerTextFontSize,
    color: theme.TextColor,
    marginBottom: 8
  },
  disableClick: {
    pointerEvents: 'none',
    opacity: 0.7
  },
  defaultInput: {
    margin: 0,
    width: '98%'
  },
  errorMessageVisible: {
    color: theme.palette.error.main,
    fontSize: theme.textFontSize,
    marginTop: '0px',
    paddingLeft: '33%',
    marginBottom: '2px'
  }
});

const getNewHoliday = (customerDetails) => {
  return {
    customerCode: customerDetails.customerCode,
    terminalId: customerDetails.terminalId,
    isCustomerSpecificHolidays: true,
    holidayId: null,
    holidayName: null,
    description: null,
    holidayStartDate: null,
    holidayEndDate: null
  };
};

const AddCustomerSpecificHoliday = ({
  classes,
  onClose,
  refreshData,
  enqueueSnackbar,
  customerDetails
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);
  const [holidayDetails, setHolidayDetails] = useState(getNewHoliday(customerDetails));
  const [holidayDetailsError, setHolidayDetailsError] = useState({
    holidayName: null,
    holidayStartDate: null,
    holidayEndDate: null
  });
  const [selectedDateRange, setSelectedDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  const getRequestData = () => {
    return {
      ...holidayDetails,
      description: sanitizeInput(holidayDetails.description),
      holidayStartDate: format(selectedDateRange[0].startDate, appConstants.APP_DATE_FORMAT),
      holidayEndDate: format(selectedDateRange[0].endDate, appConstants.APP_DATE_FORMAT)
    };
  };

  const handleAddHoliday = () => {
    const requestData = getRequestData();
    const errorInfo = getErrorInfo(requestData);
    const errorData = Object.values(errorInfo).filter((value) => value).length;
    if (!errorData) {
      setIsUpdateInProgress(true);
      postData('SaveCustomerHolidays', requestData)
        .then((response) => {
          if (response && Math.trunc(response.status / 100) === 2) {
            enqueueSnackbar(
              `Holiday '${holidayDetails.holidayName}' added successfully`,
              {
                variant: 'success',
                style:{ wordBreak: 'break-word' }
              }
            );
            refreshData(requestData?.holidayStartDate,requestData?.holidayEndDate);
            onClose([response.data.holidayId]);
          } else {
            handleError({ statusCode: response.status });
          }
        })
        .catch((err) => {
          handleError({ statusCode: err.status });
        });
    }
  };

  const handleError = ({ statusCode }) => {
    if (statusCode !== 403) {
      enqueueSnackbar(
        `Error while adding holiday '${holidayDetails.holidayName}'`, {
        variant: 'error',
        style:{ wordBreak: 'break-word' }
      });
    }
    setIsUpdateInProgress(false);
  };

  // Update holiday Details based on inputs
  const handleChange = (name) => (value) => {
    setHolidayDetails((state) => ({
      ...state,
      [name]: value
    }));
    setHolidayDetailsError((state) => ({
      ...state,
      [name]: getFieldError(name, value)
    }));
  };

  const getErrorInfo = (holidayInfo) => {
    let errorInfo = {};
    Object.keys(holidayInfo).forEach((fieldName) => {
      errorInfo[fieldName] = getFieldError(fieldName, holidayInfo[fieldName]);
    });
    if (!errorInfo.holidayStartDate || !errorInfo.holidayEndDate) {
      errorInfo = {
        ...errorInfo,
        ['holidayStartDate']: getCustomerSpecificHolidayValidations(
          customerDetails.holidays,
          holidayInfo
        )
      }; // validate date range for holiday.
    }
    setHolidayDetailsError(errorInfo);
    return errorInfo;
  };

  const updateHolidayDateRange = (dateRange) => {
    setSelectedDateRange(dateRange);
    if (!dateRange) {
      setIsDisabled(true);
      return;
    }
    updateDateRangeValidation(customerDetails.holidays, {
      ...holidayDetails,
      holidayStartDate: format(dateRange[0].startDate, appConstants.APP_DATE_FORMAT),
      holidayEndDate: format(dateRange[0].endDate, appConstants.APP_DATE_FORMAT)
    });
  };

  const updateDateRangeValidation = (holidays, newHoliday) => {
    const errorMessage = getCustomerSpecificHolidayValidations(holidays, newHoliday);
    setHolidayDetailsError((state) => ({
      ...state,
      ['holidayStartDate']: errorMessage
    }));
    setIsDisabled(Boolean(errorMessage));
  };

  const getFieldError = (field, value) => {
    if (['holidayName', 'holidayStartDate', 'holidayEndDate'].includes(field)) {
      return value?.trim() ? null : '* Required';
    }
    return null;
  };
  const isDisabledForShippers = !customerDetails.customerCode;
  return (
    <div className={`${classes.container} ${isUpdateInProgress ? classes.disableClick : null}`}>
      <span className={classes.holidayTitle}>ADD CUSTOMER SPECIFIC HOLIDAY</span>
      <TextFieldWithLabel
        label="Holiday Name :"
        id="HolidayName"
        handleChange={handleChange('holidayName')}
        className={classes.defaultInput}
        error={holidayDetailsError.holidayName}
        value={holidayDetails.holidayName ?? ''}
        inputProps={{
          maxLength: constants.MAX_HOLIDAY_LENGTH,
          pattern: ALPHA_NUMERIC_REGEX_WITH_SPACING
        }}
      />
      <CustomDateRangePickerWithLabel
        label="Holiday Date(s) :"
        selectedDateRange={selectedDateRange}
        onChangeDateRange={updateHolidayDateRange}
        minDate={new Date()}
        fullWidth
        skipMaxDate
        staticRanges={constants.STATIC_DATE_RANGE_LIST}
      />
      {(holidayDetailsError.holidayStartDate || holidayDetailsError.holidayEndDate) && (
        <p className={classes.errorMessageVisible}>
          {holidayDetailsError.holidayStartDate || holidayDetailsError.holidayEndDate}
        </p>
      )}
      <TextFieldWithLabel
        label="Description (Optional) :"
        id="Description"
        className={classes.defaultInput}
        handleChange={handleChange('description')}
        error={holidayDetailsError.description}
        value={holidayDetails.description}
        multiline
        rows={3}
        inputProps={{
          maxLength: constants.MAX_DESCRIPTION_LENGTH
        }}
      />
      <Action
        onClose={onClose}
        isDisabled={isDisabled || isDisabledForShippers}
        isUpdateInProgress={isUpdateInProgress}
        onClick={handleAddHoliday}
      />
    </div>
  );
};

export default withStyles(styles)(AddCustomerSpecificHoliday);
